<!-- SearchSoapNotes.vue -->
<script lang="ts" setup>
    import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';
    import { IPatientSoapNotesItem } from '../../lib/Models/SoapNotes';
    import { ISearchProps } from './SearchUtils';
    import { ref, onMounted, watch } from 'vue';
    import { ISearchBaseInputs } from '../../lib/Models/SearchModel';
    import * as choiceApi from '../../lib/ChoiceApi'
    import SearchBase from './SearchBase.vue';
    import { isNumber } from 'lodash';
    import { toInt } from '../../lib/Convert/String';

    type TResult = IPatientSoapNotesItem & { selectionText?: string };

    const props = withDefaults(defineProps<ISearchProps & {
        idType: DatabaseIdTypes.patient_id,
        id: number,
        defaultDaysSinceEncounter?: number,
        title?: string
    }>(), {
        label: "Soap Notes",
        defaultDaysSinceEncounter: 360,
        title: "Search based on the number of days since the encounter"
    });

    const emit = defineEmits<{
        "onChange": [selectedItem: TResult]
    }>();

    onMounted(() =>{
        internalDefaultSearchText.value = props.defaultDaysSinceEncounter?.toString();
    });

    watch(() => props.defaultSearchText, (newVal, _oldVal) =>{
        internalDefaultSearchText.value = newVal ?? "";
    });


    const processing = ref<boolean>(false);
    const internalDefaultSearchText = ref<string>("");
    const searchResults = ref<TResult[]>([]);

    const searchColumns =
        [
            { title: "Encounter", element: "encounterDate" },
            { title: "Template", element: "templateName" },
            { title: "Type", element: "soapNoteType" }
        ];
    const displayColumn = "selectionText";
    
    async function performSearchBase(inputs: ISearchBaseInputs)  {

        // Use the default days as the default days back to search
        const days = toInt(inputs.searchText) ?? props.defaultDaysSinceEncounter ?? 30;
        internalDefaultSearchText.value = days.toString();
        inputs.searchText = internalDefaultSearchText.value;

        if (props.idType !== DatabaseIdTypes.patient_id)
        {
            return;
        }

        searchResults.value = [];
        if (DatabaseIdTypes.patient_id === props.idType)
        {
            const daysSinceEncounter = parseInt(inputs.searchText ?? "");
            if (isNumber(daysSinceEncounter))
            {
                processing.value = true;

                const apiResult = await choiceApi.soapNote.patientCompletedSoapNotes(props.id, daysSinceEncounter) ?? [];
                if (apiResult)
                {
                    searchResults.value = apiResult.map(item => {
                        const uiItem = item as TResult;
                        uiItem.selectionText = `${item.templateName} (${item.encounterDate})`;
                        return item;
                    });
                }
            }
        }
        else
        {
            alert(`Expand code to handle case: ${props.idType}`);
        }
    }

    function onChange(selectedItem: TResult) {
        emit("onChange", selectedItem);
        internalDefaultSearchText.value = selectedItem?.selectionText ?? "";
    }

</script>

<template>
    <SearchBase
        ref="baseComp"
        :displayColumn="displayColumn"
        :columns="searchColumns" 
        :searchResults="searchResults"
        @performSearchBase="performSearchBase"
        @onChange="onChange"

        :title="props.title"
        :label="props.label"
        :placeholder="props.placeholder"  
        :defaultSearchText="internalDefaultSearchText"
        :processing="processing"
        :enableAutoSearch="props.enableAutoSearch"
        :showClearSearch="props.showClearSearch"
        :resetFocus="props.resetFocus"/>
</template>